import { useEffect, useState } from "react";
import { api } from "../api";
import { MOT_DATA, MKT_DATA, DONEM_DATA, TAKIM_POS, TAKIM_NEG } from "../data/kalibrasyon";

/**
 * Ana sayfa vitrini — "Piyasa Bülteni" gazete formatı.
 *
 * Amaç: kalibrasyon araştırmamızın en çarpıcı bulgularını manşet gibi sunmak.
 * Her hikâye FARKLI bir veri katmanından gelir (market edge, hafta içi, sezon
 * ritmi, motivasyon, takım radarı) ve her birinin metni kendine özeldir —
 * tek tip cümlenin farklı takımlar için tekrarı bilinçli olarak yok.
 *
 * Renk restraint ile kullanılır: krem kağıt + siyaha yakın serif mürekkep,
 * vurgu rengi yalnızca kutupluluk taşıyan sayıda (mavi = piyasa az fiyatlamış,
 * kırmızı = fazla / tersine). Kimlik renge değil etikete bağlı.
 */

// ── Palet — krem kağıt yüzeyine göre ─────────────────────────────────────────
const PAPER   = "#f8f5ee";
const INK     = "#1a1a17";
const INK_MID = "#54503f";
const INK_DIM = "#938d7a";
const RULE    = "#ddd6c4";
const MAVI    = "#1d5fbf";   // piyasa AZ fiyatlamış / gerçek daha yüksek
const KIRMIZI = "#c0392f";   // piyasa FAZLA fiyatlamış / sinyal tersine
const RUST    = "#a2542b";   // kicker aksanı

const pp = (v) => (typeof v === "number" ? v : parseFloat(v));
// Işaret HER ZAMAN gösterilir: dev sayıda çıplak "8.7" negatifi pozitiften
// ayırt ettirmiyor (renk tek başına yeterli değil). − ve + açık.
const isaret = (v) => (pp(v) > 0 ? "+" : pp(v) < 0 ? "−" : "");

// Serif başlık yığını (harici font yok)
const SERIF = "'Georgia','Times New Roman',serif";

/* ─────────────────────────────────────────────────────────────────────────
   HİKÂYELER — her biri elle yazıldı, hiçbiri şablon değil.
   Sayılar kalibrasyon modülünden (gerçek araştırma verisi) okunuyor.
   ───────────────────────────────────────────────────────────────────────── */

function bul(dataset, lig) { return dataset.find((r) => r.l === lig); }

const serieMw   = bul(DONEM_DATA, "Serie A");
const superMkt  = bul(MKT_DATA, "Süper Lig");
const scoMkt    = bul(MKT_DATA, "Scottish Prem.");
const eredDonem = bul(DONEM_DATA, "Eredivisie");
const superLider = MOT_DATA["Süper Lig"].ev.find((r) => r.s === "Şampiyon lider");

// ── Mini sezon çizgisi (D1→D5 sparkline) ─────────────────────────────────────
function SezonCizgi({ dilimler }) {
  const say = dilimler.map(pp);
  const enb = Math.max(...say.map(Math.abs), 3);
  const W = 190, H = 46, ort = H / 2;
  const x = (i) => (i / (say.length - 1)) * W;
  const y = (v) => ort - (v / enb) * (ort - 4);
  const cizgi = say.map((v, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");
  const dip = say.indexOf(Math.min(...say));
  return (
    <svg width={W} height={H + 16} style={{ display: "block", marginTop: 4 }} aria-hidden="true">
      <line x1="0" y1={ort} x2={W} y2={ort} stroke={RULE} strokeWidth="1" strokeDasharray="2 3" />
      <path d={cizgi} fill="none" stroke={INK} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
      {say.map((v, i) => (
        <circle key={i} cx={x(i)} cy={y(v)} r={i === dip ? 4 : 2.5}
          fill={i === dip ? KIRMIZI : INK} stroke={PAPER} strokeWidth="1.5" />
      ))}
      {["D1","D2","D3","D4","D5"].map((d, i) => (
        <text key={d} x={x(i)} y={H + 12} fontSize="8.5" fill={INK_DIM}
          textAnchor="middle" fontFamily="system-ui">{d}</text>
      ))}
    </svg>
  );
}

// ── Piyasa vs gerçek yatay karşılaştırma ─────────────────────────────────────
function IkiCubuk({ piyasa, gercek, renk }) {
  const enb = Math.max(piyasa, gercek, 1);
  return (
    <div style={{ display: "grid", gap: 5, marginTop: 10 }}>
      {[["PİYASA", piyasa, "#cbc4b2"], ["GERÇEK", gercek, renk]].map(([ad, v, dolgu]) => (
        <div key={ad} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 8.5, letterSpacing: ".1em", color: INK_DIM, width: 48, fontWeight: 700 }}>{ad}</span>
          <div style={{ flex: 1, height: 7, background: "#ece7da", borderRadius: 1 }}>
            <div style={{ width: `${Math.max((v / enb) * 100, 3)}%`, height: "100%", background: dolgu, borderRadius: 1 }} />
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, color: INK, width: 44, textAlign: "right",
                         fontVariantNumeric: "tabular-nums" }}>%{v}</span>
        </div>
      ))}
    </div>
  );
}

// ── Kicker (küçük renkli üst etiket) ─────────────────────────────────────────
function Kicker({ children, renk = RUST }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".16em", color: renk,
                  textTransform: "uppercase", marginBottom: 7 }}>
      {children}
    </div>
  );
}

// ── Dev sayı (manşet rakamı) ─────────────────────────────────────────────────
function DevSayi({ deger, birim = "puan", renk }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 6, lineHeight: 1 }}>
      <span style={{ fontFamily: SERIF, fontSize: 52, fontWeight: 700, color: renk,
                     fontVariantNumeric: "tabular-nums", letterSpacing: "-.02em" }}>
        {isaret(deger)}{Math.abs(pp(deger)).toFixed(1)}
      </span>
      <span style={{ fontSize: 13, color: INK_MID, fontWeight: 600 }}>{birim}</span>
    </div>
  );
}

const Manset = ({ children, size = 22 }) => (
  <h3 style={{ fontFamily: SERIF, fontSize: size, fontWeight: 700, color: INK,
               lineHeight: 1.2, letterSpacing: "-.01em", margin: 0 }}>{children}</h3>
);
const Govde = ({ children }) => (
  <p style={{ fontSize: 12.5, color: INK_MID, lineHeight: 1.62, margin: "8px 0 0" }}>{children}</p>
);
const Kaynak = ({ children }) => (
  <div style={{ fontSize: 10.5, color: INK_DIM, marginTop: 9, fontStyle: "italic" }}>{children}</div>
);

/* ── Canlı bulgu şeridi (rotasyon) — /api/insights ────────────────────────────
   Aynı takım/taraf iki kez çıkmasın, cümle kalıbı da rotasyonla değişsin.   */
const KALIP = [
  (t, taraf, p, g) => `Piyasa ${t} için ${taraf} olasılığını %${p} görüyor; sahada %${g} gerçekleşiyor.`,
  (t, taraf, p, g) => `${t}: kâğıtta %${p} ${taraf}, çimde %${g}. Aradaki fark modelimize giriyor.`,
  (t, taraf, p, g) => `Bahisçi ${t} için %${p} biçiyor — 100 maçın gerçek karnesi %${g}.`,
];
const TARAF = { home: "ev galibiyeti", away: "deplasman galibiyeti", draw: "beraberlik" };

// ── Sorgu motorunun gerçekten cevapladığı örnek sorular ─────────────────────
const ORNEK_SORULAR = [
  "İç sahada en çok beraberlik yapan takım?",
  "Son 10 maçta en çok gol atan?",
  "Deplasmanda en çok 0-0 biten takım?",
];

export default function Landing({ onKesfet, onSorgula, onTahmin }) {
  const [insights, setInsights] = useState(null);
  const [aktif, setAktif] = useState(0);

  useEffect(() => { api.insights().then(setInsights).catch(() => setInsights({ bulgular: [], kapsam: null })); }, []);
  useEffect(() => {
    const n = Math.min(insights?.bulgular?.length || 0, 5);
    if (n < 2) return;
    const t = setInterval(() => setAktif((i) => (i + 1) % n), 4500);
    return () => clearInterval(t);
  }, [insights]);

  const bulgular = insights?.bulgular || [];
  const kapsam = insights?.kapsam;
  const canli = bulgular[aktif];
  const canliMetin = canli && (() => {
    const t = canli.tur === "takim" ? canli.takim : canli.lig;
    return KALIP[aktif % KALIP.length](t, TARAF[canli.taraf] || canli.taraf, canli.piyasa, canli.gercek);
  })();

  const kolon = { paddingRight: 18, borderRight: `1px solid ${RULE}` };

  return (
    <div style={{ background: PAPER, borderRadius: 12, padding: "0 0 4px", margin: "0 auto 14px",
                  maxWidth: 1180, border: `1px solid ${RULE}`, overflow: "hidden" }}>

      {/* ══ MASTHEAD ══ */}
      <div style={{ padding: "20px 22px 14px", borderBottom: `3px double ${INK}`, margin: "0 0 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontFamily: SERIF, fontSize: 30, fontWeight: 700, color: INK, letterSpacing: "-.02em" }}>
            Piyasa Bülteni
          </div>
          <div style={{ fontSize: 11, color: INK_MID, fontWeight: 600, letterSpacing: ".04em" }}>
            YAPAY ZEKÂ · KALİBRASYON · {kapsam ? kapsam.mac_sayisi.toLocaleString("tr-TR") : "24.000+"} MAÇ
          </div>
        </div>
        <div style={{ fontSize: 12.5, color: INK_MID, marginTop: 4, fontStyle: "italic" }}>
          Bahis oranlarının sistematik olarak nerede yanıldığını ölçüyoruz — sonra tahmine katıyoruz.
        </div>
      </div>

      {/* ══ CANLI ŞERİT ══ */}
      {canliMetin && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 22px",
                      background: "#efeadc", borderBottom: `1px solid ${RULE}` }}>
          <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".12em", color: KIRMIZI,
                         border: `1px solid ${KIRMIZI}`, borderRadius: 3, padding: "2px 6px", flexShrink: 0 }}>
            CANLI
          </span>
          <span style={{ fontSize: 13, color: INK, lineHeight: 1.4 }}>{canliMetin}</span>
        </div>
      )}

      {/* ══ AKSİYON BANDI — asıl iş (tahmin) + keşfet (sorgu) ══ */}
      <div className="landing-actions"
           style={{ display: "grid", gridTemplateColumns: "minmax(0,1.1fr) minmax(0,1fr)",
                    gap: 0, borderBottom: `1px solid ${RULE}` }}>

        {/* AI TAHMİN — flagship, koyu panel */}
        <div style={{ background: INK, color: PAPER, padding: "20px 22px",
                      display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 14 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".16em",
                          color: "#e0a56a", textTransform: "uppercase", marginBottom: 8 }}>
              Asıl İşimiz · Yapay Zekâ
            </div>
            <Manset size={22}>
              <span style={{ color: PAPER }}>Bu haftanın maçını yapay zekâya sorun</span>
            </Manset>
            <p style={{ fontSize: 12.5, color: "#c9c3b4", lineHeight: 1.6, margin: "9px 0 0" }}>
              Yedi ajan formu, istatistiği, haberi ve <strong style={{ color: PAPER }}>piyasa
              sapmasını</strong> birlikte tartar; kazananı, olasılıkları ve güven skorunu üretir.
              Yukarıdaki bütün bulgular bu tahmine giriyor.
            </p>
          </div>
          <button onClick={onTahmin}
            style={{ alignSelf: "flex-start", background: "#e0a56a", color: INK, border: "none",
                     borderRadius: 7, padding: "10px 18px", fontSize: 13, fontWeight: 800,
                     cursor: "pointer", letterSpacing: ".01em" }}>
            🎯 Maç analizini başlat →
          </button>
        </div>

        {/* SORGU MOTORU — kendi analizin */}
        <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column",
                      justifyContent: "space-between", gap: 14, borderLeft: `1px solid ${RULE}` }}>
          <div>
            <Kicker renk={MAVI}>Keşfet · Kendi Analizin</Kicker>
            <Manset size={22}>Verilere kendi sorunuzu sorun</Manset>
            <p style={{ fontSize: 12.5, color: INK_MID, lineHeight: 1.6, margin: "9px 0 10px" }}>
              Lig, takım ve sezon seçin; beraberlik, gol, iç saha/deplasman kırılımıyla
              istediğiniz sıralamayı çıkarın. 24.000+ maçlık arşiv elinizin altında.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {ORNEK_SORULAR.map((q) => (
                <span key={q} onClick={onSorgula}
                  style={{ fontSize: 11, color: INK, background: "#efeadc", cursor: "pointer",
                           border: `1px solid ${RULE}`, borderRadius: 20, padding: "4px 11px" }}>
                  {q}
                </span>
              ))}
            </div>
          </div>
          <button onClick={onSorgula}
            style={{ alignSelf: "flex-start", background: "transparent", color: MAVI,
                     border: `1.5px solid ${MAVI}`, borderRadius: 7, padding: "9px 17px",
                     fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
            🎛 Sorgu motorunu aç →
          </button>
        </div>
      </div>

      {/* ══ LEAD STORY — Serie A hafta içi ══ */}
      {serieMw && (
        <div className="landing-lead"
             style={{ padding: "22px 22px 20px", borderBottom: `1px solid ${RULE}`,
                      display: "grid", gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1fr)", gap: 26 }}>
          <div>
            <Kicker renk={KIRMIZI}>Manşet · Hafta İçi Tuzağı</Kicker>
            <Manset size={27}>Salı geceleri İtalya'da ev sahibi olmak bir avantaj değil, yüktür</Manset>
            <Govde>
              Serie A'da <strong>hafta içi</strong> oynanan maçlarda ev sahibinin kazanma oranı,
              piyasanın imasının <strong>{serieMw.mw} puan</strong> altında kalıyor. Boşalan bu
              avantajın gittiği yer belli: beraberlik oranı aynı maçlarda{" "}
              <strong style={{ color: MAVI }}>{serieMw.mwb} puan</strong> yükseliyor. Yorgun kadrolar,
              seyahat, düşük tempo — piyasa bunu fiyatlamıyor, biz fiyatlıyoruz.
            </Govde>
            <Kaynak>Serie A · 1.879 maç · sezon içi 5 dilim + hafta içi ayrımı</Kaynak>
          </div>
          <div className="landing-lead-num"
               style={{ display: "flex", flexDirection: "column", justifyContent: "center",
                        borderLeft: `1px solid ${RULE}`, paddingLeft: 22 }}>
            <div style={{ fontSize: 10.5, color: INK_DIM, fontWeight: 700, letterSpacing: ".08em", marginBottom: 2 }}>
              HAFTA İÇİ EV AVANTAJI
            </div>
            <DevSayi deger={serieMw.mw} renk={KIRMIZI} />
            <div style={{ height: 1, background: RULE, margin: "14px 0" }} />
            <div style={{ fontSize: 10.5, color: INK_DIM, fontWeight: 700, letterSpacing: ".08em", marginBottom: 2 }}>
              AYNI MAÇLARDA BERABERLİK
            </div>
            <DevSayi deger={serieMw.mwb} renk={MAVI} />
          </div>
        </div>
      )}

      {/* ══ 3 KOLON: market edge · ters dünya · sezon ritmi ══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                    gap: 20, padding: "20px 22px", borderBottom: `1px solid ${RULE}` }}>
        {/* Süper Lig beraberlik */}
        {superMkt && (
          <div style={kolon}>
            <Kicker>Türkiye</Kicker>
            <Manset size={19}>Süper Lig'de beraberlik, kâğıtta göründüğünden çok daha sık</Manset>
            <div style={{ marginTop: 12 }}><DevSayi deger={superMkt.c} renk={MAVI} /></div>
            <Govde>
              1.752 maçta beraberlik, piyasanın ima ettiğini <strong>{superMkt.c} puan</strong> aşıyor —
              taranan 14 ligin en yüksek beraberlik sapması. Modelimiz Türkiye maçlarında beraberliğe ayrı ağırlık verir.
            </Govde>
          </div>
        )}
        {/* Scottish ters */}
        {scoMkt && (
          <div style={kolon}>
            <Kicker renk={KIRMIZI}>Ters Dünya</Kicker>
            <Manset size={19}>İskoçya'da bahisçinin her içgüdüsü ters teper</Manset>
            <div style={{ marginTop: 12 }}><DevSayi deger={scoMkt.c} renk={KIRMIZI} /></div>
            <Govde>
              Scottish Premiership tek istisna: büyük ev favorisi <strong>{scoMkt.a}</strong>,
              deplasman <strong>{scoMkt.b}</strong>, beraberlik <strong>{scoMkt.c} puan</strong> —
              üç sinyalin üçü de piyasanın tersine çalışıyor. Burada oranı körü körüne izlemek kaybettirir.
            </Govde>
          </div>
        )}
        {/* Sezon ritmi */}
        {eredDonem && (
          <div style={{ paddingRight: 0 }}>
            <Kicker renk={RUST}>Sezonun Ritmi</Kicker>
            <Manset size={19}>Kışın ortasında ev sahibi avantajı buharlaşıyor</Manset>
            <SezonCizgi dilimler={eredDonem.d} />
            <Govde>
              Sezonu beşe böldüğümüzde orta dilim (D3) çoğu ligde çöküyor. Hollanda'da bu düşüş{" "}
              <strong style={{ color: KIRMIZI }}>{eredDonem.d[2]} puana</strong> iniyor — takvimin
              hangi noktasında oynandığı, maçın kendisi kadar belirleyici.
            </Govde>
          </div>
        )}
      </div>

      {/* ══ 2 KOLON: motivasyon · takım radarı ══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: 22, padding: "20px 22px", borderBottom: `1px solid ${RULE}` }}>
        {/* Motivasyon */}
        {superLider && (
          <div style={kolon}>
            <Kicker renk={MAVI}>Motivasyon Okunmuyor</Kicker>
            <Manset size={19}>Zirvedeki takım, kendi sahasında piyasanın sandığından ezici</Manset>
            <IkiCubuk piyasa={superLider.mh} gercek={superLider.rh} renk={MAVI} />
            <Govde>
              Süper Lig'de şampiyonluğu kovalayan lider, evinde 10 maçın yaklaşık 7'sini kazanıyor
              (%{superLider.rh}). Piyasa aynı takımı %{superLider.mh} ile fiyatlıyor —{" "}
              <strong style={{ color: MAVI }}>
                {(superLider.rh - superLider.mh).toFixed(1)} puanlık
              </strong>{" "}
              bir kör nokta. Sıralama tablosu, orana her zaman yansımıyor.
            </Govde>
            <Kaynak>Süper Lig · lider evinde {superLider.n} maç</Kaynak>
          </div>
        )}
        {/* Takım radarı */}
        <div style={{ paddingRight: 0 }}>
          <Kicker renk={RUST}>Takım Radarı</Kicker>
          <Manset size={19}>Piyasanın yıllardır yanlış okuduğu kulüpler</Manset>
          <div style={{ marginTop: 12, display: "grid", gap: 7 }}>
            {[{ ...TAKIM_POS[0], yon: "az" }, { ...TAKIM_POS[4], yon: "az" }, { ...TAKIM_NEG[1], yon: "fazla" }].map((r) => {
              const poz = r.yon === "az";
              return (
                <div key={r.t} style={{ display: "flex", alignItems: "center", gap: 10,
                                        paddingBottom: 7, borderBottom: `1px solid ${RULE}` }}>
                  <span style={{ fontFamily: SERIF, fontSize: 19, fontWeight: 700, color: poz ? MAVI : KIRMIZI,
                                 width: 78, flexShrink: 0, fontVariantNumeric: "tabular-nums",
                                 whiteSpace: "nowrap" }}>{isaret(r.e) + Math.abs(pp(r.e)).toFixed(1) + "pp"}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>{r.t}</div>
                    <div style={{ fontSize: 10.5, color: INK_DIM }}>
                      {r.l} · {r.n} maç · piyasa {poz ? "az" : "fazla"} fiyatlıyor
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <Govde>Bazı kulüpler için sapma tek maçlık gürültü değil — 50+ maç boyunca sistemli.</Govde>
        </div>
      </div>

      {/* ══ EDİTORYAL: 7 ajan ══ */}
      <div style={{ padding: "20px 22px 22px" }}>
        <Kicker renk={INK_MID}>Nasıl Çalışıyor</Kicker>
        <div className="landing-manifesto"
             style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.15fr)", gap: 24, alignItems: "start" }}>
          <div>
            <Manset size={20}>Tek bir model değil. Sırayla konuşan yedi ajan.</Manset>
            <Govde>
              Yukarıdaki piyasa sapmaları bir tablonun süsü değil — her biri tahmin motoruna giren
              birer sinyal. Yedi ajan sırayla çalışır: form, istatistik ve haberi ayrı ayrı okur;
              ev ve deplasman için <strong>karşıt savunucular</strong> devreye girer; tahmin ajanı bu
              iki görüşü tartar; güven ajanı sonucu puanlar. Model yalnızca kimin daha iyi olduğunu
              değil, <strong>oranın nerede yanıldığını</strong> da bilir.
            </Govde>
            <div style={{ fontSize: 11.5, color: INK_MID, marginTop: 12, lineHeight: 1.6, borderTop: `1px solid ${RULE}`, paddingTop: 10 }}>
              <strong style={{ color: INK }}>Şeffaflık:</strong> Üretilen her tahmin kayda geçer ve maç
              bitince sonucuyla karşılaştırılır. İsabet oranı biriktikçe burada yayımlanacak —{" "}
              <em>henüz oynanmış maç olmadığı için bugün bir başarı yüzdesi iddia etmiyoruz.</em>
            </div>
          </div>
          <div style={{ display: "grid", gap: 5 }}>
            {[
              ["1", "Form", "Son maçlar, ev/deplasman ayrımı, gol üretimi"],
              ["2", "İstatistik", "H2H, ELO, lig pozisyonu, xG"],
              ["3", "Haber", "Sakat/cezalı, kadro, güncel gelişmeler"],
              ["4", "Ev savunucusu", "Ev sahibi lehine en güçlü argümanlar"],
              ["5", "Deplasman savunucusu", "Deplasman lehine en güçlü argümanlar"],
              ["6", "Tahmin", "Raporları tartar, olasılıkları üretir"],
              ["7", "Güven", "Tahminin ne kadar sağlam olduğunu puanlar"],
            ].map(([n, ad, ne]) => (
              <div key={n} style={{ display: "flex", gap: 11, alignItems: "baseline",
                                    padding: "6px 0", borderBottom: `1px solid ${RULE}` }}>
                <span style={{ fontFamily: SERIF, fontSize: 15, fontWeight: 700, color: RUST, width: 16, flexShrink: 0 }}>{n}</span>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: INK, width: 152, flexShrink: 0 }}>{ad}</span>
                <span style={{ fontSize: 11.5, color: INK_MID, lineHeight: 1.4 }}>{ne}</span>
              </div>
            ))}
          </div>
        </div>
        {onKesfet && (
          <div style={{ marginTop: 16, textAlign: "center" }}>
            <span onClick={onKesfet} style={{ fontSize: 12.5, fontWeight: 700, color: MAVI, cursor: "pointer",
                                              borderBottom: `1.5px solid ${MAVI}`, paddingBottom: 1 }}>
              Tüm kalibrasyon verisini incele →
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
